/**
 * Client-side validation of the draft, run before every save and on every
 * edit so problems show inline next to the field they belong to.
 *
 * Why it exists when the server validates too: `PUT /config/draft` is
 * deliberately FORGIVING per field (contracts/partner-api.md §Configs) — a
 * malformed value is normalised to its default rather than refused, and only
 * contrast failures and unknown groups are errors. Without this pass a typo'd
 * phone number would "save" and silently vanish. `normalisedFields()` is the
 * second half: after a save it compares what was sent with what the server
 * stored and reports every field the server rewrote.
 *
 * Branding contrast is not re-implemented here: `checkBrandingColors` is
 * the widget's own brandContrast.ts (the code that paints the widget), and
 * the server runs a byte-identical twin of it on save. So the portal refuses
 * exactly what the server would — only `blocking` results — and the colour a
 * note names is the colour the widget draws.
 */
import type { FieldIssue } from '../api/errors';
import {
  blackOrWhite,
  checkBrandingColors,
  contrastRatio as widgetContrastRatio,
  parseHex,
  relativeLuminance as widgetLuminance,
  type BrandingCheck,
  type BrandingCheckResult,
} from '../../../diagnosis-widget/src/brandContrast';
import {
  CONTACT_FIELDS,
  ROUTING_ACTIONS_MAX,
  ROUTING_ACTION_LABEL_MAX,
  SIZE_LIMITS,
  TRADES,
  diffPaths,
  getPath,
  type WidgetConfigModel,
} from './model';

const SHORT_TEXT_MAX = 1_000;
const TEXT_MAX = 20_000;

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const E164 = /^\+[1-9][0-9]{6,14}$/;
const POSTAL = /^[0-9]{1,5}$/;
const SLUG = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

export function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex.trim().startsWith('#') ? hex : `#${hex.trim()}`);
  return rgb ? widgetLuminance(rgb) : null;
}

export function contrastRatio(a: string, b: string): number | null {
  return parseHex(a) && parseHex(b) ? widgetContrastRatio(a, b) : null;
}

/** Black or white, whichever reads better on `background` — what the widget
 *  uses for `on_primary` on Auto. */
export function autoOnColor(background: string): string {
  return blackOrWhite(background);
}

export type ContrastCheck = BrandingCheck;
export type { BrandingCheckResult };

/** The branding contrast check, exactly as the widget renders and the server
 *  enforces it: `blocking`, `advisory` and one-click `suggestions`. */
export function brandingContrast(branding: Pick<WidgetConfigModel['branding'], 'colors' | 'theme'>): BrandingCheckResult {
  return checkBrandingColors(branding.colors, branding.theme);
}

export function contrastChecks(colors: WidgetConfigModel['branding']['colors'], theme: WidgetConfigModel['branding']['theme'] = 'auto'): ContrastCheck[] {
  return checkBrandingColors(colors, theme).checks;
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/** True when some path through the widget ends in the contact form (and so files leads). */
export function collectsContactDetails(config: WidgetConfigModel): boolean {
  if (config.routing.actions.length) {
    return config.routing.actions.some((a) => a.mode === 'partner_dispatch' || (a.mode === 'partner_booking' && !a.url));
  }
  return !['finderly_app', 'diy_only'].includes(config.routing.mode);
}

export function validateConfig(config: WidgetConfigModel): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const push = (path: string, message: string) => issues.push({ path, message });

  // Branding
  const { branding } = config;
  if (branding.logo_url && !isHttpsUrl(branding.logo_url)) push('branding.logo_url', 'Use an https:// URL.');
  for (const key of ['primary', 'on_primary', 'surface', 'text'] as const) {
    const value = branding.colors[key];
    if (value && !HEX.test(value)) push(`branding.colors.${key}`, 'Use a hex colour like #1f6f5c.');
  }
  // Only text the widget cannot repair blocks; advisory results are shown
  // as notes in the Branding section and never gate save or publish.
  for (const check of brandingContrast(branding).blocking) push(check.field, check.message ?? `${check.label} is too low in contrast.`);
  if (branding.radius_px !== null && (!Number.isInteger(branding.radius_px) || branding.radius_px < 0 || branding.radius_px > 64)) {
    push('branding.radius_px', 'Corner radius must be a whole number from 0 to 64.');
  }
  if (!branding.font_family.trim()) push('branding.font_family', 'Enter a font stack, “inherit” (your page’s font) or “system” (the widget’s own).');

  // Copy
  for (const [key, value] of Object.entries(config.copy)) {
    if (key === 'locale') continue;
    const max = key.endsWith('_md') || key.includes('message') || key.includes('confirmation') || key.includes('notice') ? TEXT_MAX : SHORT_TEXT_MAX;
    if (typeof value === 'string' && value.trim().length === 0) push(`copy.${key}`, 'Leave it unset instead of blank.');
    if (typeof value === 'string' && value.length > max) push(`copy.${key}`, `Keep this under ${max.toLocaleString()} characters.`);
  }
  if (!SLUG.test(config.copy.locale) || config.copy.locale.length > 35) push('copy.locale', 'Use a locale like en-US.');

  // Scope
  const known = new Set<string>(TRADES.map((t) => t.slug));
  for (const trade of config.scope.trades) if (!known.has(trade)) push('scope.trades', `Unknown trade “${trade}”.`);
  const area = config.scope.service_area;
  if (area.mode === 'postal_codes') {
    if (area.postal_codes.length === 0) push('scope.service_area.postal_codes', 'Add at least one ZIP or ZIP prefix, or choose “Anywhere”.');
    const bad = area.postal_codes.filter((code) => !POSTAL.test(code));
    if (bad.length) push('scope.service_area.postal_codes', `Not a ZIP or ZIP prefix: ${bad.slice(0, 5).join(', ')}.`);
    if (area.postal_codes.length > 500) push('scope.service_area.postal_codes', 'At most 500 ZIPs or prefixes.');
  }
  if (!config.scope.modes.photo && !config.scope.modes.audio && !config.scope.modes.live && !config.scope.modes.chat) {
    push('scope.modes', 'Turn on at least one way to describe the problem.');
  }
  if (config.scope.live.camera && !config.scope.modes.live) push('scope.live.camera', 'Camera applies only when live conversation is on.');
  const ids = new Set<string>();
  config.scope.custom_questions.forEach((q, i) => {
    const base = `scope.custom_questions.${i}`;
    if (!SLUG.test(q.id) || q.id.length > 64) push(`${base}.id`, 'Use letters, digits, - or _.');
    if (ids.has(q.id)) push(`${base}.id`, 'Question ids must be unique.');
    ids.add(q.id);
    if (!q.label.trim()) push(`${base}.label`, 'Every question needs a label.');
    if (q.type === 'select' && q.options.length < 2) push(`${base}.options`, 'A choice question needs at least two options.');
  });
  if (config.scope.custom_questions.length > 20) push('scope.custom_questions', 'At most 20 questions.');

  // Safety
  const { safety } = config;
  if (safety.emergency_phone && !E164.test(safety.emergency_phone)) {
    push('safety.emergency_phone', 'Use international format, e.g. +15550102000.');
  }
  if (!(safety.low_confidence_threshold >= 0 && safety.low_confidence_threshold <= 1)) {
    push('safety.low_confidence_threshold', 'Threshold must be between 0 and 1.');
  }

  // Capture
  const fieldSet = new Set<string>(CONTACT_FIELDS);
  const { capture } = config;
  for (const f of [...capture.fields.required, ...capture.fields.optional, ...capture.prefill_from_identity_token]) {
    if (!fieldSet.has(f)) push('capture.fields', `Unknown contact field “${f}”.`);
  }
  if (capture.fields.required.some((f) => capture.fields.optional.includes(f))) {
    push('capture.fields', 'A field cannot be both required and optional.');
  }
  const collectsContact = collectsContactDetails(config);
  if (collectsContact && !capture.fields.required.includes('phone_1') && !capture.fields.required.includes('email')) {
    push('capture.fields', 'Require a phone number or an email, or nobody can follow up.');
  }
  if (capture.consent.mode === 'pewc' && !capture.consent.text_md) {
    push('capture.consent.text_md', 'Written-consent mode needs the consent text shown next to the checkbox.');
  }

  // Placement size (PR #864 rules)
  const size = config.placement.size;
  const badSize = new Set<string>();
  for (const key of ['max_width_px', 'max_height_px', 'min_height_px'] as const) {
    const v = size[key];
    const { min, max } = SIZE_LIMITS[key];
    if (v !== null && (!Number.isInteger(v) || v < min || v > max)) {
      badSize.add(key);
      push(`placement.size.${key}`, `Must be a whole number from ${min} to ${max}.`);
    }
  }
  // No pair rule: the height is one number, written as min = max.
  if (size.width === 'max' && size.max_width_px === null) push('placement.size.max_width_px', `Set a maximum width (${SIZE_LIMITS.max_width_px.min}–${SIZE_LIMITS.max_width_px.max} px).`);

  // Routing
  for (const key of ['emergency', 'urgent', 'routine'] as const) {
    if (!config.routing.urgency_labels[key].trim()) push(`routing.urgency_labels.${key}`, 'Label cannot be empty.');
  }

  // Result actions (PR #865 rules)
  const actions = config.routing.actions;
  if (actions.length > ROUTING_ACTIONS_MAX) push('routing.actions', `At most ${ROUTING_ACTIONS_MAX} actions.`);
  const actionIds = new Set<string>();
  actions.forEach((a, i) => {
    const base = `routing.actions.${i}`;
    if (!SLUG.test(a.id) || a.id.length > 64) push(`${base}.id`, 'Use letters, digits, - or _ (up to 64).');
    else if (actionIds.has(a.id)) push(`${base}.id`, 'Action ids must be unique.');
    actionIds.add(a.id);
    if (!a.label.trim()) push(`${base}.label`, 'Every action needs a button label.');
    if (a.label.length > ROUTING_ACTION_LABEL_MAX) push(`${base}.label`, `Keep it under ${ROUTING_ACTION_LABEL_MAX} characters.`);
    if (a.phone !== null && a.mode !== 'partner_call') push(`${base}.phone`, 'Only a call action has a phone number.');
    if (a.phone !== null && !E164.test(a.phone)) push(`${base}.phone`, 'Use international format, e.g. +15550102000.');
    if (a.url !== null && a.mode !== 'partner_booking') push(`${base}.url`, 'Only a booking action has a link.');
    if (a.url !== null && !isHttpsUrl(a.url)) push(`${base}.url`, 'Use an https:// link.');
    for (const t of a.trades) if (!known.has(t)) push(`${base}.trades`, `Unknown trade “${t}”.`);
  });

  // Handoff
  if (config.handoff.return_url && !isHttpsUrl(config.handoff.return_url)) push('handoff.return_url', 'Use an https:// URL.');

  return issues;
}

/**
 * Fields the server rewrote while saving: what was sent differs from what it
 * stored. Reported inline as "the server did not accept this value".
 */
export function normalisedFields(sent: Partial<Record<string, unknown>>, stored: WidgetConfigModel): FieldIssue[] {
  const issues: FieldIssue[] = [];
  for (const [group, value] of Object.entries(sent)) {
    const storedGroup = getPath(stored, group);
    for (const path of diffPaths(value, storedGroup, group)) {
      // Keys the server does not model on main (extension branches) are not
      // "rewritten" — they are simply not stored by this server version.
      if (getPath(stored, path) === undefined && (path.startsWith('placement.size') || path.startsWith('routing.actions'))) continue;
      issues.push({ path, message: 'The server did not accept this value and saved its default instead.' });
    }
  }
  return issues;
}

export function issuesFor(issues: FieldIssue[], path: string): FieldIssue[] {
  return issues.filter((issue) => issue.path === path || issue.path.startsWith(`${path}.`));
}
